import React from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useWarehouseAppContext } from '../hooks/useWarehouseAppContext';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { BatchModal, SerialModal, QuantityModal, BatchQtyEditModal } from '../components/Modals';
import { WarehouseApi } from '../../../api/warehouse/generated/src';
import { getWarehouseApiConfig } from '../../../api-configs/WarehouseApiConfig';

export const OrderLayout: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = React.useState(false);
  const loadingRef = React.useRef(false);

  const {
    orders,
    selectedOrder,
    showBatchModal,
    showSerialModal,
    showQtyModal,
    showBatchQtyEditModal,
    selectedItem,
    selectedBatchIndex,
    serialInput,
    qtyInput,
    batchQtyEditInput,
    selectedBatchesTemp,
    totalRequiredQty,
    totalPickedQty,
    progress,
    allCompleted,
    hasPickedItems,
    setSerialInput,
    setQtyInput,
    setBatchQtyEditInput,
    setShowBatchModal,
    setShowSerialModal,
    setShowQtyModal,
    setShowBatchQtyEditModal,
    setSelectedItem,
    handleOrderSelect,
    addBatchToSelection,
    removeBatchFromSelection,
    getTotalSelectedQty,
    confirmBatchSelection,
    addSerialNumber,
    removeSerialNumber,
    confirmPartialSerials,
    confirmManualQty,
    confirmBatchQtyEdit,
    completeOrder,
  } = useWarehouseAppContext();

  // Load order if not already selected
  React.useEffect(() => {
    const loadOrder = async () => {
      if (!orderId) {
        navigate('/warehouse');
        return;
      }

      // If the order is already selected, no need to reload
      if (selectedOrder && selectedOrder.id === orderId) {
        loadingRef.current = false;
        return;
      }

      // Prevent multiple simultaneous loads
      if (loadingRef.current) {
        return;
      }

      // Try to find order in local state first
      const localOrder = orders.find(o => o.id === orderId);
      if (localOrder) {
        handleOrderSelect(localOrder);
        loadingRef.current = false;
        return;
      }

      // If not found locally, load from API
      loadingRef.current = true;
      setIsLoading(true);
      try {
        const warehouseApi = new WarehouseApi(getWarehouseApiConfig());
        const order = await warehouseApi.getOutboundOrderById({ orderId });
        handleOrderSelect(order);
      } catch (error) {
        console.error('Failed to load order:', error);
        navigate('/warehouse');
      } finally {
        setIsLoading(false);
        loadingRef.current = false;
      }
    };

    loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Show loading spinner while loading order
  if (isLoading || !selectedOrder) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Lade Auftrag...</p>
        </div>
      </div>
    );
  }

  // Determine current view from route
  const getCurrentView = () => {
    if (location.pathname.includes('/picklist')) return 'picklist';
    if (location.pathname.includes('/details')) return 'details';
    if (location.pathname.includes('/orders')) return 'orders';
    return 'picklist';
  };

  const handleViewChange = (view: 'picklist' | 'details' | 'orders') => {
    navigate(`/warehouse/order/${orderId}/${view}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header
        order={selectedOrder}
        onBack={() => {
          handleOrderSelect(null);
          navigate('/warehouse');
        }}
        progress={progress}
        totalPickedQty={totalPickedQty}
        totalRequiredQty={totalRequiredQty}
      />

      <div className="max-w-4xl mx-auto p-4">
        <Outlet />
      </div>

      <Footer
        currentView={getCurrentView()}
        onViewChange={handleViewChange}
        hasPickedItems={hasPickedItems}
        allCompleted={allCompleted}
        totalRequiredQty={totalRequiredQty}
        totalPickedQty={totalPickedQty}
        onCompleteOrder={completeOrder}
      />

      {/* Modals */}
      {showBatchModal && selectedItem && (
        <BatchModal
          item={selectedItem}
          selectedBatches={selectedBatchesTemp}
          onAddBatch={addBatchToSelection}
          onRemoveBatch={removeBatchFromSelection}
          getTotalSelectedQty={getTotalSelectedQty}
          onConfirm={confirmBatchSelection}
          onClose={() => {
            setShowBatchModal(false);
            setSelectedItem(null);
          }}
        />
      )}

      {showSerialModal && selectedItem && (
        <SerialModal
          item={selectedItem}
          serialInput={serialInput}
          onSerialInputChange={setSerialInput}
          onAddSerial={addSerialNumber}
          onRemoveSerial={removeSerialNumber}
          onConfirm={confirmPartialSerials}
          onClose={() => {
            setShowSerialModal(false);
            setSelectedItem(null);
            setSerialInput('');
          }}
        />
      )}

      {showQtyModal && selectedItem && (
        <QuantityModal
          item={selectedItem}
          qtyInput={qtyInput}
          onQtyChange={setQtyInput}
          onConfirm={confirmManualQty}
          onClose={() => {
            setShowQtyModal(false);
            setSelectedItem(null);
            setQtyInput('');
          }}
        />
      )}

      {showBatchQtyEditModal && selectedItem && selectedBatchIndex !== null && (
        <BatchQtyEditModal
          item={selectedItem}
          batchIndex={selectedBatchIndex}
          batchQtyEditInput={batchQtyEditInput}
          onQtyChange={setBatchQtyEditInput}
          onConfirm={confirmBatchQtyEdit}
          onClose={() => {
            setShowBatchQtyEditModal(false);
            setSelectedItem(null);
            setBatchQtyEditInput('');
          }}
        />
      )}
    </div>
  );
};
